import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import {
  sendEmailOtp,
  verifyEmailOtp,
  maskEmail,
} from '@/services/auth/mfaService';
import { getUserDetails, updateUserDetails, toCandidateApiRequest } from '@/services/api/authApi';
import { isHandledByErrorInterceptor } from '@/services/api/apiClient';
import { logout } from '@/services/auth/authService';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ROUTES } from '@/app/router/routes';
import { resolvePostLoginRedirect } from '@/services/auth/postLoginRedirect';
import { showToast } from '@/utils/globalToast';

/**
 * useMfa — orchestrates the email-based MFA flow.
 *
 * Mirrors Flutter WebMfaVerificationBloc + the WebMfaVerificationPage listener:
 *   1. Init: already verified → /home; otherwise auto-send the email OTP after 1s
 *   2. Send OTP: POST /v1/otp/generate. 429 → "too many requests" dialog, then logout → /login
 *   3. Verify OTP: POST /v1/otp/validate → JWT validation. A failure (429 included) clears
 *      the OTP and shows the message
 *   4. On success: fetch user details, set isMfaEnabled, toast otpVerifiedMsg, then the
 *      "User authenticated successfully" dialog; Continue goes to the ProfileInput destination
 *
 * Every navigation logs the screen_view Flutter's NavigationService.navigateTo() logs.
 *
 * Flutter source files:
 *   - application/web_auth/web_mfa_verification/web_mfa_verification_bloc.dart
 *   - presentation/web/auth/web_mfa_verification/web_mfa_verification_page.dart
 */

export interface MfaState {
  /** Current step in the MFA flow — 'success' shows the "User authenticated successfully" dialog */
  step: 'loading' | 'otp' | 'success';
  /** Whether an async operation is in progress */
  isLoading: boolean;
  /** Current error message — Flutter's untranslated text (empty if none) */
  error: string;
  /** Whether OTP has been sent */
  otpSent: boolean;
  /** Masked email for display (e.g. "jo****e@example.com") */
  maskedEmail: string;
  /** OTP value (controlled) */
  otp: string;
  /** Whether the OTP expiry line shows (hidden while a resend is in flight) */
  showOtpExpiry: boolean;
  /** OTP expiration countdown (seconds) */
  otpExpiresIn: number;
  /** Resend cooldown countdown (seconds) */
  resendCooldown: number;
  /** Whether the verify button should be enabled */
  canVerify: boolean;
  /** Whether the resend button should be enabled */
  canResend: boolean;
  /** Number of OTP digits expected */
  otpLength: number;
  /** 429 on send — Flutter's "Error / Too many requests" dialog */
  showTooManyRequests: boolean;

  /** Set OTP value (from input) */
  setOtp: (value: string) => void;
  /** Send/Resend OTP */
  handleSendOtp: () => Promise<void>;
  /** Verify OTP */
  handleVerifyOtp: () => Promise<void>;
  /** Logout and go to login */
  handleLogout: () => Promise<void>;
  /** OK on the too-many-requests dialog: silent logout → /login */
  handleTooManyRequestsOk: () => Promise<void>;
  /** Continue on the "User authenticated successfully" dialog */
  handleAuthenticatedContinue: () => void;
}

const OTP_LENGTH = 6;
const DEFAULT_OTP_EXPIRY_MINUTES = 5;
const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

export function useMfa(): MfaState {
  const navigate = useNavigate();
  const location = useLocation();

  const { isMfaVerified, setMfaVerified } = useAuthStore();
  const { preferences } = useTenantStore();

  // Email comes from location.state (set by useLogin, useSignup or AccountGuard)
  const locationState = location.state as { email?: string } | null;

  const email = locationState?.email ?? localStorage.getItem('userEmailId') ?? '';

  const [step, setStep] = useState<MfaState['step']>('loading');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtpValue] = useState('');
  const [showOtpExpiry, setShowOtpExpiry] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showTooManyRequests, setShowTooManyRequests] = useState(false);

  // Prevent duplicate sends
  const sendInProgress = useRef(false);

  // Tenant-configurable timers
  const otpExpiryMinutes =
    (preferences as Record<string, unknown> | null)?.['mfaOtpExpirationInMinute'] as number
    ?? DEFAULT_OTP_EXPIRY_MINUTES;
  const resendSeconds =
    (preferences as Record<string, unknown> | null)?.['mfaOtpResendInSeconds'] as number
    ?? DEFAULT_RESEND_COOLDOWN_SECONDS;

  const maskedEmailValue = maskEmail(email);

  // ── Countdown timers ──────────────────────────────────────────

  useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const id = setInterval(() => {
      setOtpExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [otpExpiresIn]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // ── Logout ────────────────────────────────────────────────────

  /** Flutter WebMfaVerificationEvent.onLogout, then navigateTo(loginRoute, arguments: profileInput). */
  const logoutToLogin = useCallback(async () => {
    try {
      setIsLoading(true);
      await logout();
    } catch {
      // continue
    } finally {
      setIsLoading(false);
      // Keep the ProfileInput so the next login returns to the same destination
      void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
      navigate(ROUTES.LOGIN, { replace: true, state: location.state });
    }
  }, [navigate, location.state]);

  const handleLogout = useCallback(async () => {
    await logoutToLogin();
  }, [logoutToLogin]);

  const handleTooManyRequestsOk = useCallback(async () => {
    setShowTooManyRequests(false);
    await logoutToLogin();
  }, [logoutToLogin]);

  // ── Send OTP ──────────────────────────────────────────────────

  /** Mirrors Flutter WebMfaVerificationBloc._OnSendEmailOTP. */
  const doSendOtp = useCallback(async () => {
    if (sendInProgress.current) return;
    sendInProgress.current = true;
    setIsLoading(true);
    setError('');

    try {
      const result = await sendEmailOtp(email);

      if (result.success) {
        setOtpSent(true);
        setStep('otp');
        setShowOtpExpiry(true);
        setOtpExpiresIn(otpExpiryMinutes * 60);
        setResendCooldown(resendSeconds);
        setOtpValue('');
      } else if (result.handledByInterceptor) {
        // The interceptor logged out / opened /duplicateAccount
      } else if (result.rateLimited) {
        setShowTooManyRequests(true);
      } else if (result.error) {
        // Flutter keeps showing its spinner after a failed send; React shows the OTP step
        // with the message so the user can resend.
        setError(result.error);
        setStep('otp');
      }
    } finally {
      setIsLoading(false);
      sendInProgress.current = false;
    }
  }, [email, otpExpiryMinutes, resendSeconds]);

  /** Resend — Flutter hides the expiry line and clears the OTP field first. */
  const handleSendOtp = useCallback(async () => {
    setShowOtpExpiry(false);
    setOtpValue('');
    await doSendOtp();
  }, [doSendOtp]);

  // ── Init (mirrors Flutter _Init for email MFA) ──
  // No "run once" ref guard here: under StrictMode the effect runs twice and the
  // first run's timer is cleared by its cleanup, so a guard would skip the send.
  // The cleanup below already ensures only one OTP request goes out.

  useEffect(() => {
    // Already MFA-verified → home (Flutter isNavigateToDashboard)
    if (isMfaVerified) {
      void analyticsService.logScreenView({ screenName: ROUTES.HOME });
      navigate(ROUTES.HOME, { replace: true, state: location.state });
      return;
    }

    // Auto-send OTP after 1 second (mirrors Flutter: Future.delayed(Duration(seconds: 1)))
    const timer = setTimeout(() => {
      doSendOtp();
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Verify OTP ─────────────────────────────────────────────────

  /** Mirrors Flutter _OnValidateEmailOTP → _OnGetUserDetails → _OnUpdateUserDetails. */
  const handleVerifyOtp = useCallback(async () => {
    if (otp.length !== OTP_LENGTH || isLoading) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await verifyEmailOtp(email, otp);

      if (result.success) {
        setMfaVerified(true);

        // Set isMfaEnabled on the candidate (updateUserDetails(apiRequest: true)). Flutter also
        // sends primaryContactNumberCountryCode '91' for every user; that is left out on purpose.
        // When either call fails Flutter's spinner never stops; React carries on, as MFA is
        // already verified.
        try {
          const userDetails = await getUserDetails();
          await updateUserDetails(toCandidateApiRequest({
            primaryEmailId: userDetails.primaryEmailId,
            workspaceId: userDetails.workspaceId,
            isMfaEnabled: true,
          }));
        } catch (detailsError) {
          if (isHandledByErrorInterceptor(detailsError)) return;
        }

        showToast('OTP verified successfully. You are now logged in', 'success', 4000);
        setStep('success');
      } else if (result.handledByInterceptor) {
        // The interceptor logged out / opened /duplicateAccount
      } else {
        setOtpValue('');
        setError(result.error ?? '');
      }
    } finally {
      setIsLoading(false);
    }
  }, [otp, email, isLoading, setMfaVerified]);

  /** Continue on the success dialog → the ProfileInput destination (WebMfaVerificationPage listener). */
  const handleAuthenticatedContinue = useCallback(() => {
    const redirect = resolvePostLoginRedirect(location.state);
    void analyticsService.logScreenView({ screenName: redirect.screenName, screenClass: redirect.screenClass });
    navigate(redirect.path, { replace: true, state: redirect.state });
  }, [navigate, location.state]);

  // ── OTP setter ─────────────────────────────────────────────────

  const setOtp = useCallback((value: string) => {
    // Only allow digits, max OTP_LENGTH characters
    const clean = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtpValue(clean);
  }, []);

  return {
    step,
    isLoading,
    error,
    otpSent,
    maskedEmail: maskedEmailValue,
    otp,
    showOtpExpiry,
    otpExpiresIn,
    resendCooldown,
    canVerify: otp.length === OTP_LENGTH && !isLoading,
    canResend: resendCooldown <= 0 && !isLoading,
    otpLength: OTP_LENGTH,
    showTooManyRequests,
    setOtp,
    handleSendOtp,
    handleVerifyOtp,
    handleLogout,
    handleTooManyRequestsOk,
    handleAuthenticatedContinue,
  };
}
